import React from 'react';
import { Link } from 'react-router-dom';
import LegalPage, { KeyPoint, Section } from './LegalPage';
import { COMMERCE, LEGAL_ENTITY, money, percent } from '../../config/legal';

const SECTIONS = [
  { id: 'summary', title: 'The short version' },
  { id: 'shipping', title: 'Shipping and delivery' },
  { id: 'window', title: 'Return window' },
  { id: 'condition', title: 'Condition we accept' },
  { id: 'how-to', title: 'How to start a return' },
  { id: 'refunds', title: 'Refunds' },
  { id: 'exchanges', title: 'Exchanges' },
  { id: 'problems', title: 'Damaged, faulty, or wrong item' },
  { id: 'not-returnable', title: 'What cannot be returned' },
  { id: 'cancelling', title: 'Cancelling before dispatch' },
  { id: 'lost', title: 'Lost or undelivered parcels' },
  { id: 'international', title: 'International orders' },
  { id: 'contact', title: 'Contact' },
];

export default function ReturnPolicy() {
  return (
    <LegalPage
      title="Returns & Shipping"
      summary="How merchandise is shipped, and how to send it back. This policy covers physical products from the Guhso shop."
      sections={SECTIONS}
    >
      <Section id="summary" title="The short version" index="1">
        <KeyPoint tone="good">
          <p>
            <strong>{COMMERCE.returnWindowDays} days to change your mind</strong> on unworn,
            unwashed merchandise in its original condition. If we sent the wrong thing or it
            arrived damaged, we cover the return and make it right at our cost.
          </p>
        </KeyPoint>
        <p>
          Memberships, donations, and directory advertising are not physical products and are
          covered by the <Link to="/terms">Terms of Service</Link> instead.
        </p>
      </Section>

      <Section id="shipping" title="Shipping and delivery" index="2">
        <p>
          Shipping is <strong>calculated at checkout from live USPS rates</strong>, based on your
          address and the size and weight of what you ordered. You choose the service you want —
          Ground Advantage, Priority Mail, or Priority Mail Express — and see the exact price
          before you pay.
        </p>
        <div className="legal-table-wrap">
          <table>
            <thead>
              <tr><th>Item</th><th>Cost</th></tr>
            </thead>
            <tbody>
              <tr>
                <td>Postage</td>
                <td>Live USPS rate for your address and parcel</td>
              </tr>
              <tr>
                <td>Orders of {money(COMMERCE.freeShippingThreshold)} or more (shippable items)</td>
                <td>
                  <strong>Free</strong> up to {money(COMMERCE.freeShippingAbsorbCap)} of postage;
                  anything above that is shown as the remaining balance
                </td>
              </tr>
              <tr>
                <td>Sales tax on taxable items</td>
                <td>{percent(COMMERCE.taxRate)}</td>
              </tr>
              <tr>
                <td>If live rates are briefly unavailable</td>
                <td>A standard {money(COMMERCE.flatShippingRate)} rate applies instead</td>
              </tr>
            </tbody>
          </table>
        </div>
        <KeyPoint>
          <p>
            Most orders of {money(COMMERCE.freeShippingThreshold)} or more ship free. On an
            unusually heavy or distant parcel, we cover the first{' '}
            {money(COMMERCE.freeShippingAbsorbCap)} and the remainder is shown clearly at
            checkout — never added afterwards.
          </p>
        </KeyPoint>
        <p>
          Orders are prepared within a few business days. Delivery estimates shown at checkout are
          estimates rather than guarantees, and carrier delays are outside our control.
        </p>
        <p>
          Please check your shipping address carefully. If a parcel is returned to us because the
          address was wrong or nobody collected it, we can resend it once you pay shipping again,
          or refund the goods less the original shipping cost.
        </p>
      </Section>

      <Section id="window" title="Return window" index="3">
        <p>
          You have <strong>{COMMERCE.returnWindowDays} days from delivery</strong> to request a
          return. Tell us within that window; the goods then need to be on their way back to us
          within 14 days of our reply.
        </p>
        <p>
          If you are in a jurisdiction with a statutory cooling-off period longer than this, that
          period applies instead — we are not trying to shorten a right you have by law.
        </p>
      </Section>

      <Section id="condition" title="Condition we accept" index="4">
        <p>Merchandise comes back to us:</p>
        <ul>
          <li>unworn and unwashed, with no smoke, fragrance, deodorant, or pet hair;</li>
          <li>with original tags attached and original packaging where there was any; and</li>
          <li>free of alterations, prints, or damage that was not there when it arrived.</li>
        </ul>
        <p>
          You may of course try something on. What we cannot resell is a garment that has been
          worn beyond that.
        </p>
        <KeyPoint>
          <p>
            If a return arrives outside this condition we will contact you before deciding. We may
            offer a partial refund reflecting the lost value, or send the item back to you at your
            cost. We will not simply keep both the goods and the money without telling you.
          </p>
        </KeyPoint>
      </Section>

      <Section id="how-to" title="How to start a return" index="5">
        <ol>
          <li>
            Email <a href={`mailto:${LEGAL_ENTITY.supportEmail}`}>{LEGAL_ENTITY.supportEmail}</a>{' '}
            with your order number, the items, and the reason.
          </li>
          <li>We reply with the return address and any instructions, usually within two business days.</li>
          <li>Send the items back with proof of postage, and give us the tracking number.</li>
          <li>We inspect on arrival and refund or exchange as agreed.</li>
        </ol>
        <KeyPoint tone="warn">
          <p>
            <strong>Please do not send anything back before contacting us.</strong> Unannounced
            returns are difficult to match to an order and slow your refund considerably.
          </p>
        </KeyPoint>
      </Section>

      <Section id="refunds" title="Refunds" index="6">
        <p>
          Refunds go back to the original payment method — we cannot refund to a different card or
          account. Once we approve a return we process the refund within{' '}
          <strong>{COMMERCE.refundBusinessDays} business days</strong>; how quickly it appears
          afterwards is up to your bank.
        </p>
        <dl>
          <dt>Goods</dt>
          <dd>Refunded in full, including the tax charged on them.</dd>

          <dt>Original shipping</dt>
          <dd>
            Refunded when the return is our fault — wrong, faulty, or damaged item. Not refunded
            when you simply changed your mind.
          </dd>

          <dt>Return postage</dt>
          <dd>
            Yours when you change your mind. Ours when the item was wrong, faulty, or damaged —
            we will either supply a label or reimburse reasonable tracked postage against a
            receipt.
          </dd>

          <dt>Free-shipping orders</dt>
          <dd>
            If returning items drops the order below{' '}
            {money(COMMERCE.freeShippingThreshold)}, we may deduct the postage the order would
            otherwise have been charged at the rate quoted when you bought it.
          </dd>
        </dl>
      </Section>

      <Section id="exchanges" title="Exchanges" index="7">
        <p>
          Happy to swap a size or colour where we have the stock. Start it the same way as a
          return and tell us what you want instead. We send the replacement once the original
          arrives and passes inspection; if the item you want has sold out, we refund instead.
        </p>
        <p>
          One exchange per item. If the replacement is not right either, it comes back as a normal
          return.
        </p>
      </Section>

      <Section id="problems" title="Damaged, faulty, or wrong item" index="8">
        <p>
          Tell us within <strong>{COMMERCE.defectReportDays} days of delivery</strong> and include
          photographs of the item and the packaging. That is not a deadline on your legal rights
          about faulty goods — it is what lets us claim against the carrier, which is the fastest
          route to sorting it out.
        </p>
        <p>
          Where an item is faulty, damaged in transit, or simply not what you ordered, we cover
          return postage and will replace it or refund you in full, including the original
          shipping. You choose which.
        </p>
        <p>
          Printed merchandise varies slightly between production runs — small differences in
          placement, shade, and finish are normal and not defects.
        </p>
      </Section>

      <Section id="not-returnable" title="What cannot be returned" index="9">
        <ul>
          <li><strong>Memberships and subscriber content</strong> — cancel any time; access runs to the end of the paid period. See the <Link to="/terms">Terms of Service</Link>.</li>
          <li><strong>Donations</strong> — gifts rather than purchases, so not refundable. Genuine mistakes are still worth telling us about.</li>
          <li><strong>Directory advertising</strong> — refundable only in the circumstances set out in the <Link to="/terms">Terms of Service</Link>.</li>
          <li><strong>Digital downloads</strong> — once downloaded, unless faulty.</li>
          <li><strong>Personalised or made-to-order items</strong> — unless faulty.</li>
          <li><strong>Gift cards</strong> and items marked <strong>final sale</strong>.</li>
          <li>Items returned without contacting us, arriving after the window, or outside the condition above — see section 4 for how we handle these.</li>
        </ul>
      </Section>

      <Section id="cancelling" title="Cancelling before dispatch" index="10">
        <p>
          Email us as soon as possible. If the order has not been packed we cancel it and refund in
          full. Once it has shipped, treat it as a return.
        </p>
      </Section>

      <Section id="lost" title="Lost or undelivered parcels" index="11">
        <p>
          If tracking has not moved for <strong>10 business days</strong>, or shows delivered but
          nothing arrived, tell us. We will open a carrier investigation, which typically takes up
          to 14 days, and then replace or refund the order.
        </p>
        <p>
          Please check with neighbours and your local depot first — that resolves most of these
          within a day.
        </p>
      </Section>

      <Section id="international" title="International orders" index="12">
        <p>
          Import duties, taxes, and customs charges are yours to pay and are not included in what
          you pay us. We cannot refund duties you have already paid, though your customs authority
          may on a returned item.
        </p>
        <p>
          Mark returned parcels clearly as <strong>“returned goods”</strong>. A return that arrives
          with duties payable by us may be refused, or the charges deducted from your refund.
        </p>
      </Section>

      <Section id="contact" title="Contact" index="13">
        <p>
          Returns and orders:{' '}
          <a href={`mailto:${LEGAL_ENTITY.supportEmail}`}>{LEGAL_ENTITY.supportEmail}</a>
          <br />
          Post: <span className="legal-todo">{LEGAL_ENTITY.address}</span>
        </p>
        <p>
          Nothing in this policy affects your statutory rights.
        </p>
      </Section>
    </LegalPage>
  );
}
